/** Catalog-driven permission editor, console analog of the dashboard's
 * AccessGroupEditPermissions: permissions grouped by scope in collapsible
 * sections, a checkbox per permission (no add-dropdown), per-scope and global
 * Grant all / Revoke all, and a search box that filters across scopes.
 *
 * Purely presentational: the parent supplies what is granted, how to toggle
 * one or many permissions, and (optionally) inline controls for granted rows.
 */
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMemo, useState, type ReactNode } from 'react';

import type { EmployeePermission } from '@/api/rbac';
import { ConfirmDialog } from '@/components/ConfirmDialog';

export function permissionScope(codename: string): string {
  const dot = codename.indexOf('.');
  return dot === -1 ? codename : codename.slice(0, dot);
}

export function permissionAction(codename: string): string {
  const dot = codename.indexOf('.');
  return dot === -1 ? codename : codename.slice(dot + 1);
}

const capitalize = (s: string) => (s ? s[0]!.toUpperCase() + s.slice(1) : s);

/** Human-readable scope aliases, mirrored from the dashboard
 *  (dashboard/src/users/agroups/utils.js SCOPES). Legacy scopes are excluded
 *  from the global "Grant all" (like the dashboard's batch helpers). */
const SCOPES: Record<string, { name: string; legacy?: boolean }> = {
  agroup: { name: 'Access group' },
  atag: { name: 'Access tag' },
  chat: { name: 'Chat' },
  import: { name: 'Import' },
  itinerary: { name: 'Itinerary' },
  notification: { name: 'Notification' },
  region: { name: 'Region' },
  sentiment_analysis: { name: 'Sentiment analysis' },
  tour: { name: 'Tour' },
  tour_operator: { name: 'Tour operator' },
  tour_participant: { name: 'Tour participant' },
  pre_trip_item: { name: 'Pre-trip item' },
  trip_type: { name: 'Trip type' },
  user: { name: 'User' },
  display_user_bio: { name: '', legacy: true },
  user_can_access_admin: { name: 'User can access admin UI', legacy: true },
  view_user_positions: { name: 'View user positions', legacy: true },
  broadcast_user_position: { name: 'Broadcast user position', legacy: true },
  launch_policy: { name: 'Launch policy' },
  ai: { name: 'AI Suite' },
  vehicle_fleet: { name: 'Vehicle fleet' },
  api_credential: { name: 'API credential' },
  activity_category: { name: 'Activity category' },
  tour_media: { name: 'Tour media' },
};

export function scopeLabel(scope: string): string {
  return SCOPES[scope]?.name || capitalize(scope);
}

const isLegacyScope = (scope: string) => Boolean(SCOPES[scope]?.legacy);

interface Props {
  catalog: EmployeePermission[];
  /** Codenames currently granted. */
  granted: Set<string>;
  onToggle: (perm: EmployeePermission, grant: boolean) => void;
  /** Bulk grant/revoke (per-scope and global buttons). `perms` is pre-filtered
   *  to the ones that actually change state. */
  onBulk: (perms: EmployeePermission[], grant: boolean) => void;
  /** Inline controls rendered under a granted row (tags/arguments/targets). */
  renderControls?: (perm: EmployeePermission) => ReactNode;
  /** Current all_tags flag of a granted tag-capable permission. Together with
   *  onBulkAllTags enables the per-scope "all tags" toggle. */
  allTagsOf?: (perm: EmployeePermission) => boolean;
  /** Set all_tags on many permissions at once (per-scope toggle). `perms` is
   *  the scope's granted tag-capable permissions. */
  onBulkAllTags?: (perms: EmployeePermission[], value: boolean) => void;
  readOnly?: boolean;
  busy?: boolean;
}

export function PermissionScopeSections({
  catalog,
  granted,
  onToggle,
  onBulk,
  renderControls,
  allTagsOf,
  onBulkAllTags,
  readOnly = false,
  busy = false,
}: Props) {
  const [search, setSearch] = useState('');
  const [openOverride, setOpenOverride] = useState<Record<string, boolean>>({});
  const [confirmRevokeAll, setConfirmRevokeAll] = useState(false);

  const sections = useMemo(() => {
    const map = new Map<string, EmployeePermission[]>();
    for (const p of catalog) {
      const scope = permissionScope(p.codename);
      const list = map.get(scope);
      if (list) list.push(p);
      else map.set(scope, [p]);
    }
    for (const list of map.values()) list.sort((a, b) => a.codename.localeCompare(b.codename));
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [catalog]);

  const q = search.trim().toLowerCase();
  const matches = (p: EmployeePermission) =>
    !q ||
    p.codename.toLowerCase().includes(q) ||
    p.name.toLowerCase().includes(q) ||
    scopeLabel(permissionScope(p.codename)).toLowerCase().includes(q);

  const grantedCount = catalog.filter((p) => granted.has(p.codename)).length;
  const disabled = readOnly || busy;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1.5} alignItems="center">
        <TextField
          size="small"
          label="Filter permissions"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 260 }}
          inputProps={{ autoComplete: 'off' }}
        />
        <Typography variant="body2" color="text.secondary">
          {grantedCount} / {catalog.length} granted
        </Typography>
        <Tooltip title={q ? 'Sections are expanded while filtering' : 'Expand all'}>
          <span>
            <IconButton
              size="small"
              disabled={Boolean(q)}
              aria-label="Expand all scopes"
              onClick={() =>
                setOpenOverride(Object.fromEntries(sections.map(([scope]) => [scope, true])))
              }
            >
              <UnfoldMoreIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title={q ? 'Sections are expanded while filtering' : 'Collapse all'}>
          <span>
            <IconButton
              size="small"
              disabled={Boolean(q)}
              aria-label="Collapse all scopes"
              onClick={() =>
                setOpenOverride(Object.fromEntries(sections.map(([scope]) => [scope, false])))
              }
            >
              <UnfoldLessIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {!readOnly && (
          <>
            <Button
              size="small"
              disabled={busy || grantedCount === catalog.length}
              onClick={() =>
                onBulk(
                  // Legacy scopes are skipped by the global grant (dashboard parity);
                  // grant them via their own section when actually needed.
                  catalog.filter(
                    (p) => !granted.has(p.codename) && !isLegacyScope(permissionScope(p.codename)),
                  ),
                  true,
                )
              }
            >
              Grant all
            </Button>
            <Button
              size="small"
              color="error"
              disabled={busy || grantedCount === 0}
              onClick={() => setConfirmRevokeAll(true)}
            >
              Revoke all
            </Button>
          </>
        )}
      </Stack>

      {sections.map(([scope, perms]) => {
        const visible = perms.filter(matches);
        if (q && visible.length === 0) return null;
        const scopeGranted = perms.filter((p) => granted.has(p.codename));
        // Searching expands everything; otherwise sections with grants start open.
        const expanded = q
          ? true
          : scope in openOverride
            ? Boolean(openOverride[scope])
            : scopeGranted.length > 0;
        return (
          <Box key={scope} sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 1.5, py: 0.75, cursor: 'pointer', bgcolor: 'action.hover' }}
              onClick={() => setOpenOverride((prev) => ({ ...prev, [scope]: !expanded }))}
            >
              {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
              <Typography variant="subtitle2" sx={{ minWidth: 140 }}>
                {scopeLabel(scope)}
              </Typography>
              <Chip
                size="small"
                variant={scopeGranted.length ? 'filled' : 'outlined'}
                color={scopeGranted.length ? 'primary' : 'default'}
                label={`${scopeGranted.length} / ${perms.length}`}
              />
              {isLegacyScope(scope) && (
                <Chip size="small" variant="outlined" label="legacy" />
              )}
              <Box sx={{ flex: 1 }} />
              {!readOnly && allTagsOf && onBulkAllTags && (() => {
                const tagPerms = scopeGranted.filter((p) => p.has_target_tags);
                if (tagPerms.length === 0) return null;
                const allOn = tagPerms.every((p) => allTagsOf(p));
                return (
                  <Tooltip title='Toggle the "all tags" flag on every granted permission in this scope'>
                    <Box
                      sx={{ display: 'flex', alignItems: 'center' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Switch
                        size="small"
                        checked={allOn}
                        disabled={busy}
                        onChange={(e) => onBulkAllTags(tagPerms, e.target.checked)}
                        inputProps={{ 'aria-label': `All tags for ${scope}` }}
                      />
                      <Typography variant="caption">all tags</Typography>
                    </Box>
                  </Tooltip>
                );
              })()}
              {!readOnly && (
                <>
                  <Button
                    size="small"
                    disabled={busy || scopeGranted.length === perms.length}
                    onClick={(e) => {
                      e.stopPropagation();
                      onBulk(perms.filter((p) => !granted.has(p.codename)), true);
                    }}
                  >
                    Grant all
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    disabled={busy || scopeGranted.length === 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      onBulk(scopeGranted, false);
                    }}
                  >
                    Revoke all
                  </Button>
                </>
              )}
            </Stack>
            <Collapse in={expanded} timeout="auto" unmountOnExit>
              <Stack spacing={0.5} sx={{ p: 1 }}>
                {visible.map((perm) => {
                  const isGranted = granted.has(perm.codename);
                  return (
                    <Box
                      key={perm.codename}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        borderRadius: 1,
                        px: 0.5,
                        bgcolor: isGranted ? 'action.selected' : undefined,
                        minHeight: 42,
                      }}
                    >
                      <Checkbox
                        size="small"
                        checked={isGranted}
                        disabled={disabled}
                        onChange={(e) => onToggle(perm, e.target.checked)}
                        inputProps={{ 'aria-label': `Grant ${perm.codename}` }}
                      />
                      <Box sx={{ minWidth: 240 }}>
                        <Typography variant="body2" sx={{ fontWeight: isGranted ? 600 : 400 }}>
                          {permissionAction(perm.codename)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {perm.name}
                        </Typography>
                      </Box>
                      {isGranted && renderControls && (
                        <Box sx={{ flex: 1, display: 'flex', gap: 2, alignItems: 'center' }}>
                          {renderControls(perm)}
                        </Box>
                      )}
                    </Box>
                  );
                })}
              </Stack>
            </Collapse>
          </Box>
        );
      })}

      <ConfirmDialog
        open={confirmRevokeAll}
        title="Revoke all permissions?"
        message={`Remove all ${grantedCount} granted permission(s)?`}
        confirmLabel="Revoke all"
        destructive
        onConfirm={() => {
          setConfirmRevokeAll(false);
          onBulk(catalog.filter((p) => granted.has(p.codename)), false);
        }}
        onCancel={() => setConfirmRevokeAll(false)}
      />
    </Stack>
  );
}
